<template>
	<div
		class="aasd781"
		:class="[size, status]"
		ref="wrapper"
		@click.self="
			$emit('focus')
			toggleOptions(true)
		"
		:data-fup="`select/${id}`"
		v-click-outside="hideOptions"
	>
		<!-- Show placeholder text only if it's empty -->
		<div
			@click="
				$emit('focus')
				toggleOptions(true)
			"
			class="placeholder"
			v-if="computedValue.length === 0 && !isVisible"
		>{{ placeholder }}</div>

		<!-- Tags -->
		<div class="tags" @click="
				$emit('focus')
				toggleOptions(true)
			">
			<div class="tag" v-for="(item, i) in computedValue" :key="i" :class="{ 'tag--full': !allowMultiselect }">
				{{ item ? item[nameKey] : "" }}
				<span class="tag__remove" @click.stop="removeTag(i)">&times;</span>
			</div>
		</div>

		<!-- Options -->
		<div class="options" :class="{ 'options--visible': isVisible }" v-if="isVisible">
			<!-- Search Input -->
			<input
				type="text"
				class="input"
				:placeholder="textSearch"
				v-model="searchTerm"
				:id="id"
				ref="input"
				v-if="allowSearch"
				@input="$emit('search', searchTerm)"
				@focus="toggleOptions(true)"
			/>

			<!-- Loading -->
			<div v-if="isLoading" class="spinner-wrapper">
				<div class="spinner"></div>
			</div>

			<!-- Options -->
			<template v-else>
				<div
					class="option"
					v-for="(option, i) in filteredOptions"
					:key="i"
					@click="
						addTag(option)
						toggleOptions(false)
					"
				>{{ option[nameKey] }}</div>
				<div v-if="filteredOptions.length === 0" class="option option--no-results">{{ textNoResults }}</div>
			</template>
		</div>
	</div>
</template>

<script>
export default {
	name: "j-base-multi-select",
	data() {
		return {
			searchTerm: "",
			isVisible: false
		}
	},
	props: {
		value: {
			required: true,
			default: () => [{ name: null, value: null }],
			validator(val) {
				return val === null || val instanceof Array
			}
		},
		options: {
			required: true,
			default: () => [],
			type: Array
		},
		size: {
			required: false,
			default: "medium",
			validator(val) {
				return (
					val === null ||
					[
						"xsmall",
						"small",
						"medium",
						"large",
						"xlarge"
					].includes(val)
				)
			}
		},
		status: {
			required: false,
			default: null,
			validator(val) {
				return (
					val === null ||
					["error", "success", "warning"].includes(val)
				)
			}
		},
		placeholder: {
			type: String,
			default: ""
		},
		id: {
			type: String
		},
		nameKey: {
			type: String,
			default: "name"
		},
		valueKey: {
			type: String,
			default: "value"
		},
		textNoResults: {
			type: String,
			required: false,
			default: "There are no results..."
		},
		textSearch: {
			type: String,
			required: false,
			default: "Search..."
		},
		allowMultiselect: {
			type: Boolean,
			default: true
		},
		allowSearch: {
			type: Boolean,
			default: true
		},
		allowLocalSearch: {
			type: Boolean,
			value: true
		},
		isLoading: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		// When user is searching, show filtered options
		filteredOptions() {
			if (!this.allowLocalSearch) return this.options
			const filteredOptions = this.options.filter(item => {
				if (
					item &&
					item[this.nameKey] &&
					item[this.nameKey]
						.toLowerCase()
						.indexOf(this.searchTerm.toLowerCase()) !==
						-1
				)
					return true
			})
			return filteredOptions
		},
		// Goes through this.value and finds names to be displayed in this.options
		// From ["HKG"] to ["Hong Kong"]
		computedValue() {
			if (!this.value) return []
			return this.value.map(item => {
				return this.options.find(
					option => option[this.valueKey] === item
				) // Find will find first match
			})
		}
	},
	methods: {
		toggleOptions(value) {
			this.isVisible = value
			this.$nextTick(function() {
				if (value === true && this.$refs.input)
					this.$refs.input.focus()
			})
		},
		hideOptions() {
			this.toggleOptions(false)
		},
		// Remove from option based on array index
		removeTag(index) {
			let newValue = [...this.value]
			newValue.splice(index, 1)
			this.$emit("input", newValue)
		},
		addTag(item) {
			let newValue = !this.value ? [] : [...this.value]
			// If multiselect option is turned off === select only one
			if (
				this.allowMultiselect === false &&
				this.computedValue.length > 0
			)
				newValue = []
			// If item is already in array do nothing
			if (newValue.includes(item[this.valueKey])) return
			newValue.push(item[this.valueKey])
			this.$emit("input", newValue)
			this.searchTerm = ""
		}
	},
	directives: {
		"click-outside": {
			bind: function(el, binding, vNode) {
				// Provided expression must evaluate to a function.
				if (typeof binding.value !== "function") {
					const compName = vNode.context.name
					let warn = `[Vue-click-outside:] provided expression '${binding.expression}' is not a function, but has to be`
					if (compName) {
						warn += `Found in component '${compName}'`
					}
					console.warn(warn)
				}
				// Define Handler and cache it on the element
				const bubble = binding.modifiers.bubble
				const handler = e => {
					if (
						bubble ||
						(!el.contains(e.target) && el !== e.target)
					) {
						binding.value(e)
					}
				}
				el.__vueClickOutside__ = handler
				// add Event Listeners
				document.addEventListener("click", handler)
			},
			unbind: function(el, binding) {
				// Remove Event Listeners
				document.removeEventListener(
					"click",
					el.__vueClickOutside__
				)
				el.__vueClickOutside__ = null
			}
		}
	}
}
</script>

<style src="./index.css" scoped />